import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import useSocket from "@shared/features/socket/useSocket";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getPricePrecision } from "@shared/features/portfolio/utils";
import { PrecisionType, SocketTopics } from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { toFixed } from "@shared/utils/numbers";
import { skipToken } from "@reduxjs/toolkit/query";
import { isContractsSymbol } from "@shared/utils/misc";

const usePricesSocket = (symbol?: string, exchange?: string) => {
  const appTheme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);

  const args = useMemo(() => {
    return symbol && exchange
      ? { symbol, exchange, isAsset: !isContractsSymbol(symbol) }
      : skipToken;
  }, [symbol, exchange]);

  const socketPrices = useSocket<SocketTopics.PRICES>(
    SocketTopics.PRICES,
    args,
    undefined,
    true
  );

  const price = socketPrices?.value;
  const previousPrice = socketPrices?.previous;
  let socketPriceValue: number | undefined;
  let socketPricePrevious: number | undefined;

  if (typeof price === "object") {
    socketPriceValue =
      displayCurrency === DISPLAY_CURRENCIES.USD ? price?.USD : price?.BTC;
  } else {
    socketPriceValue = price;
  }
  if (typeof previousPrice === "object") {
    socketPricePrevious =
      displayCurrency === DISPLAY_CURRENCIES.USD
        ? previousPrice?.USD
        : previousPrice?.BTC;
  } else {
    socketPricePrevious = previousPrice;
  }

  const socketPrice = useMemo(
    () => ({
      value: socketPriceValue,
      previous: socketPricePrevious,
    }),
    [socketPriceValue]
  );

  let { precision } = usePrecisions(
    symbol && exchange
      ? {
          symbol,
          exchange,
          precisionType: PrecisionType.PRICE,
        }
      : undefined
  );
  if (args !== skipToken && args.isAsset && socketPriceValue)
    precision = getPricePrecision(socketPriceValue, displayCurrency);

  const socketPriceValueWithPrecision = toFixed(socketPriceValue, precision);
  const socketPricePreviousWithPrecision = toFixed(
    socketPricePrevious,
    precision
  );

  const getPriceColor = (textColor?: string) => {
    if (socketPricePreviousWithPrecision && socketPriceValueWithPrecision) {
      if (socketPriceValueWithPrecision < socketPricePreviousWithPrecision) {
        return appTheme.colors.red;
      } else if (
        socketPriceValueWithPrecision > socketPricePreviousWithPrecision
      ) {
        return appTheme.colors.green;
      }
    }
    return textColor || appTheme.colors.primaryText;
  };

  return {
    socketPrice,
    getPriceColor,
    timestamp: socketPrices?.timestamp ?? 0,
  };
};

export default usePricesSocket;
